import React, { useMemo, useState } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { DriverPushLapEntry } from "../../../types/fpAnalysis";
import { getPreferredFormattedTimeMs } from "../../../utils/preferences";

// Colors for loops
const LOOP_COLORS = [
  "#E6194B",
  "#3CB44B",
  "#FFE119",
  "#4363D8",
  "#F58231",
  "#BB66FF",
  "#46F0F0",
  "#FF66FF",
];

export default function PowerModeLeaderboard({
  driverAllPushLaps,
  powerModeLabel,
  useCombinedLoops = true,
}: {
  driverAllPushLaps: Record<string, DriverPushLapEntry[]>;
  powerModeLabel: string;
  useCombinedLoops?: boolean;
}) {
  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const timingLoops = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.track?.info?.timingLoops,
    isEqual,
  );

  const strategyData = useSelector(
    (state: RootState) => state.imports.imports.strategy?.data,
    isEqual,
  );

  // Sort column state: "time" or loop index (number for combined loops, string for regular loops)
  const [sortColumn, setSortColumn] = useState<"time" | number | string>(
    "time",
  );

  // Calculate fastest lap per driver from all their push laps
  const driverFastestPushLap = useMemo(() => {
    const result: Record<string, DriverPushLapEntry> = {};

    Object.entries(driverAllPushLaps || {}).forEach(([driver, laps]) => {
      if (laps && laps.length > 0) {
        const fastestLap = laps.reduce((fastest, current) =>
          current.lapTime < fastest.lapTime ? current : fastest,
        );
        result[driver] = fastestLap;
      }
    });

    return result;
  }, [driverAllPushLaps]);

  // Get sorted loop indices and calculate all the data
  const {
    loopIndices,
    loopDisplayNames,
    loopCompositions,
    driverPerLoopTimes,
    driverCombinedLoopTimes,
    shouldUseCombinedLoops,
  } = useMemo(() => {
    // Get all loop indices from timingLoops, with loop 0 (FL) at the end
    const loopIndices = timingLoops
      ? Object.keys(timingLoops)
          .map((k) => parseInt(k, 10))
          .filter((n) => !isNaN(n))
          .sort((a, b) => {
            // Loop 0 (FL) goes last
            if (a === 0) return 1;
            if (b === 0) return -1;
            return a - b;
          })
          .map(String)
      : [];

    // Build a map from loop name to data key
    const loopNameToDataKey: Record<string, string> = {};
    loopIndices.forEach((loopIdx) => {
      const loopInfo = timingLoops?.[loopIdx as `${number}`];
      const loopName = loopInfo?.name;
      if (loopName) {
        loopNameToDataKey[loopName] = loopIdx === "0" ? "8" : loopIdx;
      }
    });

    // Get combined loop configuration from strategy import
    const combinedLoops: { label: string; loopNames: string[] }[] = [];
    for (let i = 1; i <= 8; i++) {
      const loopNamesArray = strategyData?.[`combinedLoop${i}`] as
        | (string | null)[]
        | undefined;
      const label = strategyData?.[`combinedLoopLabel${i}`] as
        | string
        | undefined;

      if (label && loopNamesArray) {
        const validLoopNames = loopNamesArray.filter(
          (name): name is string => name !== null && name !== undefined,
        );
        if (validLoopNames.length > 0) {
          combinedLoops.push({ label, loopNames: validLoopNames });
        }
      }
    }

    // Use combined loops if enabled via prop AND config exists
    const shouldUseCombinedLoops = useCombinedLoops && combinedLoops.length > 0;

    // Get display names for loops
    const loopDisplayNames = shouldUseCombinedLoops
      ? combinedLoops.map((c) => c.label)
      : loopIndices.map((loopIdx) => {
          const loopInfo = timingLoops?.[loopIdx as `${number}`];
          return loopInfo?.name || `L${loopIdx}`;
        });

    // Map timing loop index to data key (loop 0 in timing = key "8" in data)
    const getDataKey = (timingLoopIdx: string) =>
      timingLoopIdx === "0" ? "8" : timingLoopIdx;

    // Convert cumulative times to per-loop times
    const driverPerLoopTimes: Record<string, Record<string, number>> = {};
    Object.entries(driverFastestPushLap).forEach(([driverKey, entry]) => {
      driverPerLoopTimes[driverKey] = {};
      let prevCumulativeTime = 0;
      loopIndices.forEach((loop) => {
        const dataKey = getDataKey(loop);
        const cumulativeTime = entry.loops[dataKey];
        if (cumulativeTime !== undefined) {
          driverPerLoopTimes[driverKey][dataKey] =
            cumulativeTime - prevCumulativeTime;
          prevCumulativeTime = cumulativeTime;
        }
      });
    });

    // Calculate combined loop times for each driver
    const driverCombinedLoopTimes: Record<string, Record<number, number>> = {};
    if (shouldUseCombinedLoops) {
      Object.entries(driverPerLoopTimes).forEach(
        ([driverKey, perLoopTimes]) => {
          driverCombinedLoopTimes[driverKey] = {};
          combinedLoops.forEach((combined, idx) => {
            let totalTime = 0;
            let hasAllLoops = true;
            for (const loopName of combined.loopNames) {
              const dataKey = loopNameToDataKey[loopName];
              if (dataKey && perLoopTimes[dataKey] !== undefined) {
                totalTime += perLoopTimes[dataKey];
              } else {
                hasAllLoops = false;
                break;
              }
            }
            if (hasAllLoops) {
              driverCombinedLoopTimes[driverKey][idx] = totalTime;
            }
          });
        },
      );
    }

    // Build loop compositions (e.g., "IP2 + TV3") for combined loops
    const loopCompositions = shouldUseCombinedLoops
      ? combinedLoops.map((c) => c.loopNames.join("+"))
      : [];

    return {
      loopIndices,
      loopDisplayNames,
      loopCompositions,
      driverPerLoopTimes,
      driverCombinedLoopTimes,
      shouldUseCombinedLoops,
    };
  }, [driverFastestPushLap, timingLoops, strategyData, useCombinedLoops]);

  // Calculate fastest time for each loop (for delta calculation when sorting by loop)
  const fastestLoopTimes = useMemo(() => {
    const fastest: Record<string | number, number> = {};

    if (shouldUseCombinedLoops) {
      // For combined loops, find min for each combined loop index
      Object.values(driverCombinedLoopTimes).forEach((driverLoops) => {
        Object.entries(driverLoops).forEach(([idx, time]) => {
          const key = parseInt(idx, 10);
          if (fastest[key] === undefined || time < fastest[key]) {
            fastest[key] = time;
          }
        });
      });
    } else {
      // For regular loops, find min for each loop data key
      Object.values(driverPerLoopTimes).forEach((driverLoops) => {
        Object.entries(driverLoops).forEach(([dataKey, time]) => {
          if (fastest[dataKey] === undefined || time < fastest[dataKey]) {
            fastest[dataKey] = time;
          }
        });
      });
    }

    return fastest;
  }, [driverCombinedLoopTimes, driverPerLoopTimes, shouldUseCombinedLoops]);

  // Sort drivers based on selected column
  const sortedDrivers = useMemo(() => {
    const sessionDrivers = Object.keys(driverAllPushLaps || {});
    return sessionDrivers.sort((a, b) => {
      let aTime: number | undefined;
      let bTime: number | undefined;

      if (sortColumn === "time") {
        aTime = driverFastestPushLap[a]?.lapTime;
        bTime = driverFastestPushLap[b]?.lapTime;
      } else if (typeof sortColumn === "number" && shouldUseCombinedLoops) {
        // Combined loop index
        aTime = driverCombinedLoopTimes[a]?.[sortColumn];
        bTime = driverCombinedLoopTimes[b]?.[sortColumn];
      } else {
        // Regular loop data key
        aTime = driverPerLoopTimes[a]?.[sortColumn as string];
        bTime = driverPerLoopTimes[b]?.[sortColumn as string];
      }

      // Drivers without times go to the bottom
      if (aTime === undefined && bTime === undefined) return 0;
      if (aTime === undefined) return 1;
      if (bTime === undefined) return -1;
      return aTime - bTime;
    });
  }, [
    driverFastestPushLap,
    driverAllPushLaps,
    sortColumn,
    driverCombinedLoopTimes,
    driverPerLoopTimes,
    shouldUseCombinedLoops,
  ]);

  // Get leader lap time for delta calculation
  const leaderLapTime =
    sortedDrivers.length > 0
      ? driverFastestPushLap[sortedDrivers[0]]?.lapTime
      : 0;

  return (
    <div className={styles.container}>
      <div className={styles.leaderboardTable}>
        <table>
          <thead>
            <tr className={styles.titleRow}>
              <th colSpan={5 + loopDisplayNames.length}>
                {powerModeLabel} Leaderboard
              </th>
            </tr>
            {shouldUseCombinedLoops && loopCompositions.length > 0 && (
              <tr className={styles.compositionRow}>
                <th colSpan={5}></th>
                {loopCompositions.map((composition, idx) => {
                  const isSelected = sortColumn === idx;
                  return (
                    <th
                      key={idx}
                      className={styles.clickableHeader}
                      style={{
                        color: isSelected
                          ? "#000"
                          : LOOP_COLORS[idx % LOOP_COLORS.length],
                        backgroundColor: isSelected ? "#fff" : undefined,
                      }}
                      onClick={() => setSortColumn(idx)}
                    >
                      {composition}
                    </th>
                  );
                })}
              </tr>
            )}
            <tr>
              <th>P</th>
              <th>Drv</th>
              <th>L</th>
              <th
                className={styles.clickableHeader}
                style={{
                  backgroundColor: sortColumn === "time" ? "#fff" : undefined,
                  color: sortColumn === "time" ? "#000" : undefined,
                }}
                onClick={() => setSortColumn("time")}
              >
                Time
              </th>
              <th>Delta</th>
              {shouldUseCombinedLoops
                ? loopDisplayNames.map((name, idx) => {
                    const isSelected = sortColumn === idx;
                    return (
                      <th
                        key={idx}
                        className={styles.clickableHeader}
                        style={{
                          color: isSelected
                            ? "#000"
                            : LOOP_COLORS[idx % LOOP_COLORS.length],
                          backgroundColor: isSelected ? "#fff" : undefined,
                        }}
                        onClick={() => setSortColumn(idx)}
                      >
                        {name}
                      </th>
                    );
                  })
                : loopDisplayNames.map((name, idx) => {
                    const dataKey =
                      loopIndices[idx] === "0" ? "8" : loopIndices[idx];
                    const isSelected = sortColumn === dataKey;
                    return (
                      <th
                        key={idx}
                        className={styles.clickableHeader}
                        style={{
                          color: isSelected
                            ? "#000"
                            : LOOP_COLORS[idx % LOOP_COLORS.length],
                          backgroundColor: isSelected ? "#fff" : undefined,
                        }}
                        onClick={() => setSortColumn(dataKey)}
                      >
                        {name}
                      </th>
                    );
                  })}
            </tr>
          </thead>
          <tbody>
            {sortedDrivers.map((driver, position) => {
              const driverInfoEntry = driverInfo?.[driver];
              const driverName = driverInfoEntry?.driverName || driver;
              const lapTime = driverFastestPushLap[driver]?.lapTime;
              const lapNumber = driverFastestPushLap[driver]?.lapNumber;
              const deltaToLeader =
                lapTime !== undefined && leaderLapTime !== undefined
                  ? lapTime - leaderLapTime
                  : undefined;
              const driverLoops = driverPerLoopTimes[driver] || {};
              const driverCombinedLoops = driverCombinedLoopTimes[driver] || {};

              const rowStyle = {
                color: driverInfoEntry?.driverFontColour || "white",
                backgroundColor: driverInfoEntry?.driverTeamColour || "#333",
              };

              return (
                <tr key={driver} style={rowStyle}>
                  <td>{lapTime !== undefined ? position + 1 : "-"}</td>
                  <td>{driverName}</td>
                  <td>{lapNumber !== undefined ? lapNumber : "-"}</td>
                  <td>
                    {lapTime !== undefined
                      ? getPreferredFormattedTimeMs(lapTime)
                      : "-"}
                  </td>
                  <td>
                    {lapTime === undefined ||
                    position === 0 ||
                    deltaToLeader === undefined
                      ? "-"
                      : `${deltaToLeader >= 0 ? "+" : ""}${(
                          deltaToLeader / 1000
                        ).toFixed(3)}`}
                  </td>
                  {shouldUseCombinedLoops
                    ? loopDisplayNames.map((_, idx) => {
                        const loopTime = driverCombinedLoops[idx];
                        const isSelected = sortColumn === idx;
                        const fastestTime = fastestLoopTimes[idx];
                        const deltaToFastest =
                          isSelected &&
                          loopTime !== undefined &&
                          fastestTime !== undefined
                            ? loopTime - fastestTime
                            : undefined;
                        return (
                          <td key={idx}>
                            {loopTime === undefined
                              ? "-"
                              : isSelected && deltaToFastest !== undefined
                              ? deltaToFastest === 0
                                ? getPreferredFormattedTimeMs(loopTime)
                                : `${deltaToFastest >= 0 ? "+" : ""}${(
                                    deltaToFastest / 1000
                                  ).toFixed(3)}`
                              : getPreferredFormattedTimeMs(loopTime)}
                          </td>
                        );
                      })
                    : loopIndices.map((loop) => {
                        // Convert timing loop index to data key (loop 0 = key "8")
                        const dataKey = loop === "0" ? "8" : loop;
                        const loopTime = driverLoops[dataKey];
                        const isSelected = sortColumn === dataKey;
                        const fastestTime = fastestLoopTimes[dataKey];
                        const deltaToFastest =
                          isSelected &&
                          loopTime !== undefined &&
                          fastestTime !== undefined
                            ? loopTime - fastestTime
                            : undefined;
                        return (
                          <td key={loop}>
                            {loopTime === undefined
                              ? "-"
                              : isSelected && deltaToFastest !== undefined
                              ? deltaToFastest === 0
                                ? getPreferredFormattedTimeMs(loopTime)
                                : `${deltaToFastest >= 0 ? "+" : ""}${(
                                    deltaToFastest / 1000
                                  ).toFixed(3)}`
                              : getPreferredFormattedTimeMs(loopTime)}
                          </td>
                        );
                      })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
